import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Green3, PrimaryTitleGray } from '../../app/theme/tokens/colors';
import { AiOutlinePlusSquare, AiOutlineMinusSquare } from 'react-icons/ai';

const TableWidget = styled.table`
  width: 100%;
  text-align: left;

  .table-column-resize-handle {
    position: absolute;
    top: 0;
    right: -4px;
    z-index: 2;
    width: 8px;
    height: 100%;
    cursor: col-resize;
    touch-action: none;
  }

  .table-column-resize-handle::after {
    position: absolute;
    top: 15%;
    left: 3px;
    width: 2px;
    height: 70%;
    content: '';
    background: ${Green3};
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  th:hover .table-column-resize-handle::after,
  .table-column-resize-handle:hover::after {
    opacity: 1;
  }
`;

const RowWidget = styled.tr`
  font-size: 12px;
  color: ${PrimaryTitleGray};
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

const RowWidgetExp = styled.tr`
  font-size: 12px;
  color: ${PrimaryTitleGray};
`;

interface TableProps {
    height?: string | number;
    tableStyle?: React.CSSProperties;
    columnWidths?: Array<string | number>;
    columnResize?: {
        columns: Record<number, { minWidth: number; maxWidth: number }>;
        onResizeEnd: (columnIndex: number, width: number) => void;
        getAutoWidth?: (columnIndex: number) => number | null;
    };
    expandable?: boolean;
    header: any[];
    data: any[];
    onRowSelection?: (row: any, index: number) => void;
    isRowDisabled?: (row: any, index: number) => boolean;
}

export const Table: React.FC<TableProps> = ({
    height,
    tableStyle,
    columnWidths,
    columnResize,
    expandable,
    header,
    data,
    onRowSelection,
    isRowDisabled,
}) => {
    const [currentSelectedRowIdx, setCurrentSelectedRowIdx] =
        useState<number>(-1);
    const tableRef = useRef<HTMLTableElement>(null);
    const currentWidthsRef = useRef<Record<number, number>>({});
    const resizeCleanupRef = useRef<(() => void) | null>(null);
    const resizeFrameRef = useRef<number | null>(null);

    const filterExpandable = (colName: string) => {
        if (!expandable) return true;
        return colName !== 'expandPanel' && colName !== 'showExpandPanelIcon';
    };

    const onRowSelected = (row: any, idx: number) => {
        if (isRowDisabled?.(row, idx)) {
            return;
        }
        setCurrentSelectedRowIdx(idx);
        if (onRowSelection) {
            onRowSelection(row, idx);
        }
    };

    const checkBox =
        header[0] &&
        Object.prototype.hasOwnProperty.call(header[0], 'props') &&
        header[0].props?.children &&
        header[0].props.children.props &&
        header[0].props.children.props.type === 'checkbox';

    const isSmall = (h: any) => {
        try {
            const className = `${h.props.className}`;
            return className.toLowerCase() === 'small';
        } catch (e) {
            return false;
        }
    };

    const getColumnWidth = (idx: number) => {
        const width = columnWidths?.[idx];
        if (width === undefined) return undefined;
        const fallback = typeof width === 'number' ? `${width}px` : width;
        return columnResize?.columns[idx]
            ? `var(--table-column-${idx}-width, ${fallback})`
            : fallback;
    };

    const getWidthInPixels = (width: string | number | undefined) => {
        if (typeof width === 'number') return width;
        const parsed = Number.parseFloat(String(width ?? ''));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const getColumnVariableName = (idx: number) => `--table-column-${idx}-width`;

    const getTotalWidth = () =>
        (columnWidths ?? []).reduce<number>(
            (total, width, idx) => total + (currentWidthsRef.current[idx] ?? getWidthInPixels(width)),
            0
        );

    const applyColumnWidth = (idx: number, width: number) => {
        const table = tableRef.current;
        if (!table) return;

        currentWidthsRef.current[idx] = width;
        table.style.setProperty(getColumnVariableName(idx), `${width}px`);
        table.style.minWidth = `${getTotalWidth()}px`;
    };

    useEffect(() => {
        const nextWidths: Record<number, number> = {};
        (columnWidths ?? []).forEach((width, idx) => {
            nextWidths[idx] = getWidthInPixels(width);
        });
        currentWidthsRef.current = nextWidths;
    }, [columnWidths]);

    useEffect(() => {
        return () => {
            resizeCleanupRef.current?.();
            if (resizeFrameRef.current !== null) {
                window.cancelAnimationFrame(resizeFrameRef.current);
            }
        };
    }, []);

    const startColumnResize = (event: React.PointerEvent<HTMLSpanElement>, idx: number) => {
        const rules = columnResize?.columns[idx];
        if (!rules || event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();

        const startWidth = currentWidthsRef.current[idx] ?? getWidthInPixels(columnWidths?.[idx]);
        const startX = event.clientX;
        let pendingWidth = startWidth;
        let cleanedUp = false;
        const previousCursor = document.body.style.cursor;
        const previousUserSelect = document.body.style.userSelect;

        const clampWidth = (width: number) =>
            Math.min(rules.maxWidth, Math.max(rules.minWidth, Math.round(width)));

        const flushWidth = () => {
            resizeFrameRef.current = null;
            applyColumnWidth(idx, pendingWidth);
        };

        const handleMove = (moveEvent: PointerEvent) => {
            pendingWidth = clampWidth(startWidth + moveEvent.clientX - startX);
            if (resizeFrameRef.current === null) {
                resizeFrameRef.current = window.requestAnimationFrame(flushWidth);
            }
        };

        let handleEnd: () => void = () => undefined;

        const cleanUp = () => {
            if (cleanedUp) return;
            cleanedUp = true;
            if (resizeFrameRef.current !== null) {
                window.cancelAnimationFrame(resizeFrameRef.current);
                resizeFrameRef.current = null;
            }
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleEnd);
            document.body.style.cursor = previousCursor;
            document.body.style.userSelect = previousUserSelect;
            resizeCleanupRef.current = null;
        };

        handleEnd = () => {
            if (resizeFrameRef.current !== null) {
                window.cancelAnimationFrame(resizeFrameRef.current);
                resizeFrameRef.current = null;
            }
            applyColumnWidth(idx, pendingWidth);
            columnResize?.onResizeEnd(idx, pendingWidth);
            cleanUp();
        };

        resizeCleanupRef.current?.();
        resizeCleanupRef.current = cleanUp;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleEnd, { once: true });
    };

    const autoSizeColumn = (event: React.MouseEvent<HTMLSpanElement>, idx: number) => {
        const rules = columnResize?.columns[idx];
        const measuredWidth = columnResize?.getAutoWidth?.(idx);
        if (!rules || !measuredWidth) return;

        event.preventDefault();
        event.stopPropagation();

        const width = Math.min(rules.maxWidth, Math.max(rules.minWidth, Math.round(measuredWidth)));
        applyColumnWidth(idx, width);
        columnResize?.onResizeEnd(idx, width);
    };

    const tableWidgetStyle = {
        height: height ? height : 'auto',
        ...tableStyle,
    } as React.CSSProperties & Record<string, string>;

    Object.keys(columnResize?.columns ?? {}).forEach((columnIndex) => {
        const idx = Number(columnIndex);
        const width = getWidthInPixels(columnWidths?.[idx]);
        tableWidgetStyle[getColumnVariableName(idx)] = `${width}px`;
    });

    return (
        <TableWidget ref={tableRef} style={tableWidgetStyle}>
            <thead className="table-style">
                <RowWidget
                    style={{ backgroundColor: '#f2f2f2', color: PrimaryTitleGray }}
                >
                    {header.map((h, idx) =>
                        (checkBox && idx === 0) || isSmall(h) ? (
                            <th
                                style={{
                                    boxSizing: 'border-box',
                                    width: getColumnWidth(idx) ?? '0%',
                                    textAlign: 'center',
                                    position: columnResize?.columns[idx] ? 'relative' : undefined,
                                }}
                                key={idx}
                            >
                                {h}
                                {columnResize?.columns[idx] ? (
                                    <span
                                        aria-label={`Redimensionar coluna ${idx + 1}`}
                                        className="table-column-resize-handle"
                                        onPointerDown={(event) => startColumnResize(event, idx)}
                                        onDoubleClick={(event) => autoSizeColumn(event, idx)}
                                        role="separator"
                                    />
                                ) : null}
                            </th>
                        ) : (
                            <th
                                style={{
                                    boxSizing: 'border-box',
                                    width: getColumnWidth(idx),
                                    textAlign: 'center',
                                    position: columnResize?.columns[idx] ? 'relative' : undefined,
                                }}
                                key={idx}
                            >
                                {h}
                                {columnResize?.columns[idx] ? (
                                    <span
                                        aria-label={`Redimensionar coluna ${idx + 1}`}
                                        className="table-column-resize-handle"
                                        onPointerDown={(event) => startColumnResize(event, idx)}
                                        onDoubleClick={(event) => autoSizeColumn(event, idx)}
                                        role="separator"
                                    />
                                ) : null}
                            </th>
                        )
                    )}
                </RowWidget>
            </thead>
            <tbody className="table-style">
                {data.map((row, idx) => {
                    if (onRowSelection) {
                        const disabled = isRowDisabled?.(row, idx) ?? false;

                        return (
                            <RowWidget
                                key={idx}
                                style={{
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    backgroundColor:
                                        !disabled && currentSelectedRowIdx === idx ? Green3 : '',
                                    color:
                                        !disabled && currentSelectedRowIdx === idx ? 'white' : '',
                                    opacity: disabled ? 0.65 : 1,
                                }}
                                onClick={() => {
                                    onRowSelected(row, idx);
                                }}
                            >
                                {Object.keys(row)
                                    .filter(filterExpandable)
                                    .filter((x) => row[x] !== null)
                                    .map((f, i) => (
                                        <td
                                            style={{
                                                padding: '5px',
                                                boxSizing: 'border-box',
                                                width: getColumnWidth(i),
                                            }}
                                            key={i}
                                        >
                                            {row[f]}
                                        </td>
                                    ))}
                            </RowWidget>
                        );
                    }

                    return (
                        <RowWidget key={idx} data-key={idx}>
                            {Object.keys(row)
                                .filter(filterExpandable)
                                .filter((x) => row[x] !== null)
                                .map((f, i) => (
                                    <td
                                        style={{
                                            padding: '5px',
                                            boxSizing: 'border-box',
                                            width: getColumnWidth(i),
                                        }}
                                        key={i}
                                    >
                                        {row[f] === undefined || typeof row[f] !== 'string'
                                            ? row[f]
                                            : row[f].split('\n').map((p: string, j: number) => (
                                                <div key={j}>
                                                    <span>{p}</span>
                                                    <br />
                                                </div>
                                            ))}
                                    </td>
                                ))}
                        </RowWidget>
                    );
                })}
            </tbody>
        </TableWidget>
    );
};

interface ExpandableTableProps {
    height?: string | number;
    expandable?: boolean;
    header: any[];
    data: any[];
}

export const ExpandableTable: React.FC<ExpandableTableProps> = ({
    height,
    expandable,
    header,
    data,
}) => {
    const filterExpandable = (colName: string) => {
        if (!expandable) return true;
        return (
            colName !== 'expandPanel' &&
            colName !== 'showExpandPanelIcon' &&
            colName !== 'addExpandedRow' &&
            colName !== 'removeExpandedRow'
        );
    };

    const [expandVisible, setExpandVisible] = useState<boolean[]>(
        data.map(() => false)
    );

    const rows: any[] = [];
    data.forEach((el) => {
        rows.push(el);
        rows.push({ panel: el.expandPanel, expandablePanel: true });
    });

    const toggleExpandPanel = (idx: number, row: any) => {
        if (!expandVisible[idx]) row.addExpandedRow();
        else row.removeExpandedRow();

        const updated = [...expandVisible];
        updated[idx] = !updated[idx];
        setExpandVisible(updated);
    };

    return (
        <TableWidget style={{ height: height ? height : 'auto' }}>
            <thead className="table-style">
                <RowWidgetExp
                    style={{ backgroundColor: '#f2f2f2', color: PrimaryTitleGray }}
                >
                    {header.map((h, idx) =>
                        h === '' ? (
                            <th style={{ boxSizing: 'border-box', width: '0%' }} key={idx}>
                                {h}
                            </th>
                        ) : (
                            <th style={{ boxSizing: 'border-box' }} key={idx}>
                                {h}
                            </th>
                        )
                    )}
                    {expandable && <th></th>}
                </RowWidgetExp>
            </thead>
            <tbody className="table-style">
                {rows.map((row, idx) =>
                    row.expandablePanel ? (
                        <RowWidgetExp
                            key={idx}
                            style={{ display: expandVisible[idx] ? '' : 'none' }}
                        >
                            <td
                                colSpan={header.length + 1}
                                style={{ paddingLeft: '10px', boxSizing: 'border-box' }}
                            >
                                {row.panel}
                            </td>
                        </RowWidgetExp>
                    ) : (
                        <RowWidgetExp
                            key={idx}
                            data-key={idx}
                            style={{ backgroundColor: idx % 4 === 0 ? '' : '#f2f2f2' }}
                        >
                            {Object.keys(row)
                                .filter(filterExpandable)
                                .map((f, i) =>
                                    row[f] !== row.sinalLuminoso ? (
                                        <td
                                            style={{ padding: '10px', boxSizing: 'border-box' }}
                                            key={i}
                                        >
                                            {row[f]}
                                        </td>
                                    ) : (
                                        <td
                                            style={{ padding: '0px', boxSizing: 'border-box' }}
                                            key={i}
                                        >
                                            {row[f]}
                                        </td>
                                    )
                                )}
                            <td>
                                {expandVisible[idx + 1] ? (
                                    <AiOutlineMinusSquare
                                        onClick={() => toggleExpandPanel(idx + 1, row)}
                                        style={{
                                            fontSize: '18px',
                                            cursor: 'pointer',
                                            display: row.showExpandPanelIcon === false ? 'none' : '',
                                        }}
                                    />
                                ) : (
                                    <AiOutlinePlusSquare
                                        onClick={() => toggleExpandPanel(idx + 1, row)}
                                        style={{
                                            fontSize: '18px',
                                            cursor: 'pointer',
                                            display: row.showExpandPanelIcon === false ? 'none' : '',
                                        }}
                                    />
                                )}
                            </td>
                        </RowWidgetExp>
                    )
                )}
            </tbody>
        </TableWidget>
    );
};

interface ControlledExpandableTableProps {
    expandable?: boolean;
    header: any[];
    data: any[];
    expandVisible: boolean[];
}

export const ControlledExpandableTable: React.FC<
    ControlledExpandableTableProps
> = ({ expandable, header, data, expandVisible }) => {
    const filterExpandable = (colName: string) => {
        if (!expandable) return true;
        return colName !== 'expandPanel';
    };

    const rows: any[] = [];
    data.forEach((el) => {
        rows.push(el);
        rows.push({ panel: el.expandPanel, expandablePanel: true });
    });

    return (
        <TableWidget>
            <thead className="table-style">
                <RowWidgetExp
                    style={{ backgroundColor: '#f2f2f2', color: PrimaryTitleGray }}
                >
                    {header.map((h, idx) => (
                        <th
                            style={{ paddingLeft: '10px', boxSizing: 'border-box' }}
                            key={idx}
                        >
                            {h}
                        </th>
                    ))}
                </RowWidgetExp>
            </thead>
            <tbody className="table-style">
                {rows.map((row, idx) =>
                    row.expandablePanel ? (
                        <RowWidgetExp
                            key={idx}
                            style={{ display: expandVisible[idx] ? '' : 'none' }}
                        >
                            <td
                                colSpan={header.length + 1}
                                style={{ paddingLeft: '10px', boxSizing: 'border-box' }}
                            >
                                {row.panel}
                            </td>
                        </RowWidgetExp>
                    ) : (
                        <RowWidgetExp
                            key={idx}
                            data-key={idx}
                            style={{ backgroundColor: idx % 4 === 0 ? '' : '#f2f2f2' }}
                        >
                            {Object.keys(row)
                                .filter(filterExpandable)
                                .map((f, i) => (
                                    <td
                                        style={{ padding: '10px', boxSizing: 'border-box' }}
                                        key={i}
                                    >
                                        {row[f]}
                                    </td>
                                ))}
                        </RowWidgetExp>
                    )
                )}
            </tbody>
        </TableWidget>
    );
};
